import React from 'react';
import { motion } from 'framer-motion';
import { HiBriefcase, HiCalendar, HiLocationMarker } from 'react-icons/hi';

const Experience: React.FC = () => {
  const experiences = [
    {
      title: 'Senior Software Developer',
      company: 'Australian Taxation Office',
      location: 'Australia',
      period: 'Aug 2024 – Present',
      description: 'Business application enhancements, new features, and technical leadership for frontend and backend upgrades.',
      implementation: 'Led front end upgrade from Angular v14 to v18 and Bootstrap v3.5 to v5.2',
      achievements: [
        'Led front end upgrade from Angular v14 to v18 and Bootstrap v3.5 to v5.2',
        'Upgraded machine authorization application and automation test project to Java 21',
        'Assisted QA with integration tests and Jenkins deployments',
        'Troubleshot and resolved sprint issues, peer reviews, and integration testing',
        'Followed agile methodologies and contributed to sprint planning and standups'
      ],
      technologies: ['.NET Core', 'Angular', 'Bootstrap', 'Java 21', 'Spring Boot', 'Jenkins']
    },
    {
      title: 'Senior Software Developer',
      company: 'Department of Child Protection',
      location: 'Australia',
      period: 'Apr 2024 – Aug 2024',
      description: 'Full stack development for web applications reporting child abuses and system notifications.',
      implementation: 'Developed UI components with Svelte JS',
      achievements: [
        'Developed UI components with Svelte JS',
        'Ensured UI responsiveness and accessibility',
        'Implemented CI/CD pipeline using Azure DevOps',
        'Created release procedures and branch/release policies'
      ],
      technologies: ['Svelte', 'Azure DevOps', 'CI/CD']
    },
    {
      title: 'Senior Software Developer',
      company: 'TCCD (Clemenger Group Retail Services - CGLRS)',
      location: 'Australia',
      period: 'Dec 2023 – Apr 2024',
      description: 'Standalone developer for product planning and promotion web application for FMCG products.',
      implementation: 'Designed and implemented solution architecture',
      achievements: [
        'Designed and implemented solution architecture',
        'Worked with ASP.NET Core 6, Entity Framework, DevExpress, SQL Server, Azure Data Factory, Azure Key Vault, Angular 12',
        'Followed agile methodologies and participated in standups'
      ],
      technologies: ['ASP.NET Core 6', 'Entity Framework', 'DevExpress', 'SQL Server', 'Azure Data Factory', 'Azure Key Vault', 'Angular 12']
    },
    {
      title: 'Technical Team Lead',
      company: 'Databrain Pty Ltd',
      location: 'Australia',
      period: 'Jun 2023 – Dec 2023',
      description: 'Led development and enhancement of web applications for resource tracking and timesheet management.',
      implementation: 'Deployed scalable microservices with AWS Lambda, CDK, and Fargate for robust, cloud-native solutions.',
      achievements: [
        'Implemented roles authentication and solution design',
        'Worked with ASP.NET Core 6, GraphQL, AWS CDK, AWS CodePipeline, Postgres SQL, Entity Framework, Angular 12',
        'Managed timesheet system in Python on AWS Lambda',
        'Set up penetration testing environment and performance middleware',
        'Provided estimates and ran daily standups and Scrum events'
      ],
      technologies: ['ASP.NET Core 6', 'GraphQL', 'AWS CDK', 'AWS CodePipeline', 'Postgres SQL', 'Entity Framework', 'Angular 12', 'Python', 'AWS Lambda']
    },
    {
      title: 'Senior Full Stack Developer',
      company: 'SAAB Australia',
      location: 'Australia',
      period: 'Sep 2022 – Feb 2023',
      description: 'Standalone developer for records migration and evaluation project.',
      implementation: 'Designed and developed records migration project',
      achievements: [
        'Designed and developed records migration project',
        'Worked with ASP.NET Core 6, Azure Blob Storage, Azure CI/CD, SQL Server, Entity Framework, Angular 12, Bootstrap CSS',
        'Created unit and integration tests, provided estimates for enhancements'
      ],
      technologies: ['ASP.NET Core 6', 'Azure Blob Storage', 'Azure CI/CD', 'SQL Server', 'Entity Framework', 'Angular 12', 'Bootstrap CSS']
    },
    {
      title: 'Senior Full Stack Developer',
      company: 'Standards Australia',
      location: 'Australia',
      period: 'Mar 2022 – Sep 2022',
      description: 'Software development team member for sprint tasks and enhancements.',
      implementation: 'Designed event-driven architecture using Azure Event Queues for scalable, decoupled systems.',
      achievements: [
        'Delivered features using ASP.NET Core, Web API, React, Postgres DB, Azure Event Queues, Azure Service Bus, Azure CI/CD, SQL Server, Entity Framework, jQuery',
        'Handled release deployments and led team on critical issues',
        'Performed code reviews and provided technical solutions for sprint tasks'
      ],
      technologies: ['ASP.NET Core', 'Web API', 'React', 'Postgres DB', 'Azure Event Queues', 'Azure Service Bus', 'Azure CI/CD', 'SQL Server', 'Entity Framework', 'jQuery']
    },
    {
      title: 'Senior Full Stack Developer',
      company: 'Hummgroup (formerly Flexigroup Pty Ltd)',
      location: 'Australia',
      period: 'Nov 2018 – Mar 2022',
      description: 'Support and product development for financial services applications.',
      implementation: 'Implemented robust messaging for business-critical flows using RabbitMQ.',
      achievements: [
        'Handled day-to-day production issues and delivered hotfixes',
        'Developed APIs and deployment documentation',
        'Led team for critical issues and product development',
        'Acted as SME for technical and domain issues'
      ],
      technologies: ['ASP.NET MVC', 'ASP.NET Core', 'Web API', 'Azure CI/CD', 'Rabbit MQ', 'Aurelia', 'jQuery', 'JavaScript', 'Oracle', 'SQL Server']
    },
    {
      title: 'Senior Full Stack Developer',
      company: 'SA Structural',
      location: 'Australia',
      period: 'Aug 2018 – Oct 2018',
      description: 'Web application development for end user needs and UI/UX improvements.',
      implementation: 'Analyzed requirements and designed new web pages',
      achievements: [
        'Analyzed requirements and designed new web pages',
        'Implemented color and theme skins for user experience',
        'Developed with HTML, CSS, ASP.NET Core, jQuery, JavaScript, SQL Server',
        'Maintained web standards and collaborated with Design Architect'
      ],
      technologies: ['HTML', 'CSS', 'ASP.NET Core', 'jQuery', 'JavaScript', 'SQL Server']
    }
  ];

  return (
    <section id="experience" className="section-padding bg-white">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-dark-900 mb-4">Work Experience</h2>
          <p className="text-lg text-dark-600 max-w-2xl mx-auto">
            My professional journey and the impact I've made in various roles
          </p>
        </motion.div>

        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 top-0 bottom-0 w-0.5 bg-gray-200"></div>

          {experiences.map((experience, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`relative mb-12 ${
                index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
              }`}
            >
              <div className={`flex flex-col md:flex-row items-start gap-8 ${
                index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
              }`}>
                {/* Timeline dot */}
                <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-4 h-4 bg-primary-600 rounded-full border-4 border-white shadow-lg"></div>

                {/* Content */}
                <div className={`bg-white rounded-xl p-6 shadow-lg card-hover ml-12 md:ml-0 md:w-1/2 ${
                  index % 2 === 0 ? 'md:mr-auto md:pr-8' : 'md:ml-auto md:pl-8'
                }`}>
                  {/* Header */}
                  <div className="mb-4">
                    <h3 className="text-xl font-semibold text-dark-900 mb-2">
                      {experience.title}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-dark-600 mb-3">
                      <div className="flex items-center gap-1">
                        <HiBriefcase size={16} />
                        <span>{experience.company}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <HiLocationMarker size={16} />
                        <span>{experience.location}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <HiCalendar size={16} />
                        <span>{experience.period}</span>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-dark-600 mb-4 leading-relaxed">
                    {experience.description}
                    {experience.implementation && (
                      <span className="block text-primary-700 mt-2 text-sm font-medium">{experience.implementation}</span>
                    )}
                  </p>

                  {/* Achievements */}
                  <div className="mb-4">
                    <h4 className="font-semibold text-dark-900 mb-2">Key Achievements:</h4>
                    <ul className="space-y-1">
                      {experience.achievements.map((achievement, achievementIndex) => (
                        <li key={achievementIndex} className="text-sm text-dark-600 flex items-start gap-2">
                          <span className="text-primary-600 mt-1">•</span>
                          {achievement}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Technologies */}
                  <div>
                    <h4 className="font-semibold text-dark-900 mb-2">Technologies:</h4>
                    <div className="flex flex-wrap gap-2">
                      {experience.technologies.map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience; 